import { get } from 'svelte/store';

import {
  defaultFilters,
  hasSearched,
  queryFilters,
  queryResult,
  resultStats,
  scoreFilterRange,
  selectionScope
} from '$lib/stores/selection';
import { selectedIndustryCentroids } from '$lib/stores/data';
import { neighborSamples } from '$lib/stores/neighbors';
import { mixedResults } from '$lib/stores/mixedResults';
import { resetGuided } from '$lib/stores/guided';
import { resetHydration } from '$lib/stores/hydration';
import { discardList } from '$lib/api/lists';

/**
 * Starting again.
 *
 * # Why this is one function
 *
 * A selection is spread over eight stores — the filters, the industries, the
 * examples, the results, their statistics, the score band, the mixed set, the
 * guide's own position — and every place that wanted to clear it cleared a
 * different subset. The mode switcher cleared five of them on a direct switch
 * and eight behind the confirmation dialog, which is why switching mode
 * sometimes left the previous search's rows on screen under a new mode's
 * heading.
 *
 * # What survives
 *
 * The contact mode, and nothing else. It is a preference about how rows are
 * delivered rather than a criterion, so it outlives the selection it was set
 * during.
 *
 * # The uploaded list goes with it
 *
 * It used to stay: it was work to produce, it has its own code, and only the
 * subtraction was switched off. That was right while a list was the last
 * optional step of a search — a way to avoid buying rows twice.
 *
 * The list door made a list something a whole selection can be derived from.
 * Keeping it through a reset would leave the customer with the file that shaped
 * the filters they just cleared, still held, still ticked into nothing they can
 * see — and, since the file is their own customer base, still on our server
 * after they said start again. See discardList.
 */
export function resetSelection(): void {
  const filters = defaultFilters();

  // How many people come with each company outlives a selection: it is how the
  // customer buys, not what they are looking for.
  filters.contact_mode = get(queryFilters).contact_mode ?? 'single';

  queryFilters.set(filters);
  selectedIndustryCentroids.set(new Map());
  neighborSamples.set([]);

  queryResult.set([]);
  resultStats.set({});
  // Back to "not searched yet", which is what makes the guide the screen a
  // fresh selection shows rather than "nothing found".
  hasSearched.set(false);
  scoreFilterRange.set([0, 100]);
  // Nothing is seated, so nothing constrains what a filter may draw from.
  selectionScope.set(null);
  mixedResults.set({ companies: [], metadata: {}, similarityBatches: [], loading: false });
  resetHydration();

  resetGuided();

  // The list and the subtraction both go. discardList clears the stores at
  // once and deletes on the server behind the call, so this stays synchronous
  // for the three places that reset without awaiting anything.
  discardList();
}

// The navigation that goes with a reset — assistant closed, picker closed,
// back to the guide's first question — deliberately stays in the layout rather
// than moving here beside the clearing.
//
// `$app/navigation` does not resolve outside a SvelteKit component, so a store
// that imports `goto` cannot be unit tested: it fails to load, and takes every
// test of the clearing down with it. The stores empty state; the components
// decide where the customer then is.
